import { createContext, useContext, useMemo, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchShutterStates } from '../services/ioBroker'
import { useSubscriberRegistry, useSubscribed } from '../hooks/useSubscribers'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''
const fetchShutterConfig = () => fetch(`${API_BASE}/settings`).then(r => r.json()).then(s => s.shutterConfig ?? null)
const fetchShellyDevices = () => fetch(`${API_BASE}/shelly/devices`).then(r => r.json()).then(d => Array.isArray(d) ? d : []).catch(() => [])

const ShutterStatesContext = createContext(null)
const ShutterConfigContext = createContext(null)

/**
 * Shutter positions (polled every 10 s only while a view that shows them is mounted)
 * plus the configuration. Code that only needs the configuration uses
 * useShutterConfig() and does not start the polling.
 */
export function useShutter() {
  const states = useSubscribed(ShutterStatesContext, 10_000)
  const config = useContext(ShutterConfigContext)
  return useMemo(() => ({ shutterData: states?.shutterData ?? null, ...config }), [states, config])
}

export function useShutterConfig() {
  return useContext(ShutterConfigContext)
}

export function ShutterProvider({ children }) {
  const qc = useQueryClient()
  const subs = useSubscriberRegistry()

  const shutterQ        = useQuery({ queryKey: ['shutterStates'],  queryFn: fetchShutterStates, enabled: subs.interval != null, refetchInterval: subs.interval ?? false })
  const shutterConfigQ  = useQuery({ queryKey: ['shutterConfig'],  queryFn: fetchShutterConfig })
  const shellyDevicesQ  = useQuery({ queryKey: ['shellyDevices'],  queryFn: fetchShellyDevices })

  const refreshShutterConfig = useCallback(
    () => qc.invalidateQueries({ queryKey: ['shutterConfig'] }),
    [qc],
  )

  const states = useMemo(() => ({
    subscribe:   subs.subscribe,
    shutterData: shutterQ.data ?? null,
  }), [subs.subscribe, shutterQ.data])
  const config = useMemo(() => ({
    shutterConfig:         shutterConfigQ.data  ?? null,
    shellyDevices:         shellyDevicesQ.data  ?? [],
    refreshShutterConfig,
  }), [shutterConfigQ.data, shellyDevicesQ.data, refreshShutterConfig])

  return (
    <ShutterConfigContext.Provider value={config}>
      <ShutterStatesContext.Provider value={states}>{children}</ShutterStatesContext.Provider>
    </ShutterConfigContext.Provider>
  )
}
